import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Button,
  TextField,
  Chip,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Save as SaveIcon,
  Assignment as ProjectIcon,
  AddCircleOutline as AddIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  HelpOutline as HelpIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Language as GlobeIcon,
  Shield as ShieldIcon,
  ReceiptLong as ReceiptIcon,
  NotificationsActive as NotificationIcon,
  Check as CheckIcon,
  Speed as SpeedIcon,
  History as HistoryIcon,
  AutoAwesome as SparklesIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { db } from '../../services/firebase';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  addDoc,
  query,
  where,
  limit
} from 'firebase/firestore';
import ClientInvoicesTab from './invoicing/ClientInvoicesTab';

const CustomTextField = ({ ...props }) => (
  <TextField
    fullWidth
    variant="outlined"
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        color: '#F5F7FA',
        bgcolor: 'rgba(255, 255, 255, 0.03)',
        '& fieldset': { borderColor: '#1E293B' },
        '&:hover fieldset': { borderColor: '#334155' },
        '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
      },
      '& .MuiInputLabel-root': { color: '#94A3B8' },
      '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
      ...props.sx
    }}
  />
);

export default function ProjectWorkspace() {
  const { currentUser } = useAuth();
  const { themeMode } = useTheme();
  const isLight = themeMode === 'light';

  // Dynamic Theme Colors
  const bgSurface = isLight ? '#ffffff' : '#0D111A';
  const bgCardSubtle = isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.02)';
  const borderCard = isLight ? '#e2e8f0' : '#1E293B';
  const borderSubtle = isLight ? '#e2e8f0' : 'rgba(247, 247, 248, 0.08)';
  const textPrimary = isLight ? '#0f172a' : '#FFFFFF';
  const textSecondary = isLight ? '#475569' : '#94A3B8';
  const textMuted = isLight ? '#64748b' : '#64748B';

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Linked Client Record from Firestore `clients` collection
  const [clientData, setClientData] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [gstRequested, setGstRequested] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Project Details State (editable by Client)
  const [projectData, setProjectData] = useState({
    projectName: '',
    tagline: '',
    stage: 'In Development',
    targetAudience: '',
    techScope: '',
    stagingUrl: '',
    repoUrl: '',
    primaryContactName: '',
    primaryContactPhone: '',
    notes: '',
  });

  // Client Requests State
  const [requests, setRequests] = useState([]);
  const [newRequest, setNewRequest] = useState({
    title: '',
    category: 'Feature Enhancement',
    priority: 'Standard',
    description: '',
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [showAddRequest, setShowAddRequest] = useState(false);

  // Client Invoices State
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Sync tab selection from query parameters (e.g. /project?tab=invoices)
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const tabParam = searchParams.get('tab');
      if (tabParam === 'invoices' || tabParam === 'billing') {
        setActiveTab(4);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    async function loadProject() {
      if (!currentUser) return;
      try {
        setLoading(true);
        // 1. Load user doc from Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        let userData = {};
        let clientId = null;

        if (userSnap.exists()) {
          userData = userSnap.data();
          clientId = userData.assignedClientId || null;
        }

        // 2. Fetch corresponding client document from `clients` collection
        let fetchedClient = null;
        if (clientId) {
          try {
            const clientSnap = await getDoc(doc(db, 'clients', clientId));
            if (clientSnap.exists()) {
              fetchedClient = { id: clientSnap.id, ...clientSnap.data() };
            }
          } catch (err) {
            console.warn('Direct client lookup failed:', err);
          }
        }

        // Fallback query if assignedClientId was missing on user doc
        if (!fetchedClient) {
          try {
            const qEmail = query(collection(db, 'clients'), where('email', '==', currentUser.email), limit(1));
            const snapEmail = await getDocs(qEmail);
            if (!snapEmail.empty) {
              fetchedClient = { id: snapEmail.docs[0].id, ...snapEmail.docs[0].data() };
              clientId = fetchedClient.id;
            } else if (userData.company) {
              const qComp = query(collection(db, 'clients'), where('company', '==', userData.company), limit(1));
              const snapComp = await getDocs(qComp);
              if (!snapComp.empty) {
                fetchedClient = { id: snapComp.docs[0].id, ...snapComp.docs[0].data() };
                clientId = fetchedClient.id;
              }
            }
          } catch (err) {
            console.warn('Fallback client query error:', err);
          }
        }

        setClientData(fetchedClient);

        // 3. Pre-fill project specifications from user doc or client doc
        const existingProject = userData.projectDetails || {};
        const stagingFallback = fetchedClient?.domainInfo?.stagingDomain
          ? (fetchedClient.domainInfo.stagingDomain.startsWith('http') ? fetchedClient.domainInfo.stagingDomain : `https://${fetchedClient.domainInfo.stagingDomain}`)
          : '';
        const primaryFallback = fetchedClient?.domainInfo?.primaryDomain
          ? (fetchedClient.domainInfo.primaryDomain.startsWith('http') ? fetchedClient.domainInfo.primaryDomain : `https://${fetchedClient.domainInfo.primaryDomain}`)
          : (fetchedClient?.website || '');

        setProjectData({
          projectName: existingProject.projectName || fetchedClient?.company || userData.company || `${userData.displayName || 'Client'}'s Startup`,
          tagline: existingProject.tagline || fetchedClient?.companyDescription || userData.bio || '',
          stage: existingProject.stage || (fetchedClient?.projectStatus === 'live' ? 'Live' : fetchedClient?.domainInfo?.sslStatus === 'active' ? 'Testing / Staging' : 'In Development'),
          targetAudience: existingProject.targetAudience || (fetchedClient?.industry ? `Target Industry: ${fetchedClient.industry}` : ''),
          techScope: existingProject.techScope || (fetchedClient?.services?.length ? `Active Services: ${fetchedClient.services.join(', ')}` : ''),
          stagingUrl: existingProject.stagingUrl || stagingFallback || '',
          repoUrl: existingProject.repoUrl || primaryFallback || '',
          primaryContactName: existingProject.primaryContactName || fetchedClient?.name || fetchedClient?.primaryContact || userData.displayName || '',
          primaryContactPhone: existingProject.primaryContactPhone || fetchedClient?.phone || userData.phone || '',
          notes: existingProject.notes || (fetchedClient?.domainInfo?.deploymentNotes ? `Deployment status: ${fetchedClient.domainInfo.deploymentNotes}` : ''),
        });

        // 4. Load client checklists & real activity logs if clientId found
        if (clientId) {
          try {
            const chkQuery = query(collection(db, 'client_checklists'), where('clientId', '==', clientId));
            const chkSnap = await getDocs(chkQuery);
            setChecklists(chkSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          } catch (chkErr) {
            console.warn('Error loading checklists:', chkErr);
          }

          try {
            const logsQuery = query(collection(db, 'client_activity_logs'), where('clientId', '==', clientId));
            const logsSnap = await getDocs(logsQuery);
            const logs = logsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            logs.sort((a, b) => {
              const timeA = a.timestamp?.seconds || (a.timestamp ? new Date(a.timestamp).getTime() : 0);
              const timeB = b.timestamp?.seconds || (b.timestamp ? new Date(b.timestamp).getTime() : 0);
              return timeB - timeA;
            });
            setActivityLogs(logs);
          } catch (logErr) {
            console.warn('Error loading activity logs:', logErr);
          }
        }

        // 5. Load client project requests
        try {
          const reqsColRef = collection(db, 'users', currentUser.uid, 'projectRequests');
          const reqsSnap = await getDocs(reqsColRef);
          const reqList = reqsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setRequests(reqList);
          if (reqList.some(r => r.category === 'GST Integration' || r.title.toLowerCase().includes('gst'))) {
            setGstRequested(true);
          }
        } catch (e) {
          console.warn('No custom requests subcollection yet:', e);
        }

        // 6. Load official client invoices
        try {
          setLoadingInvoices(true);
          let invList = [];

          if (clientId) {
            const invQuery = query(
              collection(db, 'client_invoices'),
              where('clientId', '==', clientId)
            );
            const invSnap = await getDocs(invQuery);
            invList = invSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          }

          // Fallback if client has matching email or company
          if (invList.length === 0 && currentUser.email) {
            try {
              const invEmailQuery = query(
                collection(db, 'client_invoices'),
                where('clientEmail', '==', currentUser.email)
              );
              const invEmailSnap = await getDocs(invEmailQuery);
              invList = invEmailSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            } catch (fallbackErr) {
              console.warn('Fallback invoice query notice:', fallbackErr);
            }
          }

          // Filter out unpublished drafts from client workspace
          const publishedInvoices = invList.filter(inv => inv.publishedToClient !== false);

          // Sort descending by issue date / timestamp
          publishedInvoices.sort((a, b) => {
            const timeA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.issueDate ? new Date(a.issueDate).getTime() : 0);
            const timeB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.issueDate ? new Date(b.issueDate).getTime() : 0);
            return timeB - timeA;
          });

          setInvoices(publishedInvoices);
        } catch (invErr) {
          console.warn('Error loading client invoices:', invErr);
        } finally {
          setLoadingInvoices(false);
        }
      } catch (err) {
        console.error('Error loading project details:', err);
      } finally {
        setLoading(false);
      }
    }

    loadProject();
  }, [currentUser]);

  const handleProjectFieldChange = (e) => {
    const { name, value } = e.target;
    setProjectData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProject = async () => {
    if (!currentUser) return;
    try {
      setSaving(true);
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, {
        projectDetails: {
          ...projectData,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
        }
      }, { merge: true });

      setSnackbar({
        open: true,
        message: 'Project details updated successfully! Our engineering team will review your updates.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving project:', err);
      setSnackbar({ open: true, message: 'Failed to update project: ' + err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!newRequest.title.trim()) {
      setSnackbar({ open: true, message: 'Please specify a request title', severity: 'error' });
      return;
    }

    try {
      setSubmittingRequest(true);
      const reqsColRef = collection(db, 'users', currentUser.uid, 'projectRequests');
      const docData = {
        title: newRequest.title.trim(),
        category: newRequest.category,
        priority: newRequest.priority,
        description: newRequest.description.trim(),
        status: 'Submitted',
        createdAt: new Date().toISOString(),
        clientEmail: currentUser.email,
      };

      const docRef = await addDoc(reqsColRef, docData);
      setRequests(prev => [{ id: docRef.id, ...docData }, ...prev]);
      setNewRequest({
        title: '',
        category: 'Feature Enhancement',
        priority: 'Standard',
        description: '',
      });
      setShowAddRequest(false);
      setSnackbar({
        open: true,
        message: 'Request submitted successfully! Our lead architect has been notified.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error submitting request:', err);
      setSnackbar({ open: true, message: 'Failed to submit request: ' + err.message, severity: 'error' });
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleCopyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
    setSnackbar({
      open: true,
      message: '✓ Client portal link copied to clipboard!',
      severity: 'success'
    });
  };

  const handleRequestGstIntegration = async () => {
    if (!currentUser) return;
    try {
      const reqsColRef = collection(db, 'users', currentUser.uid, 'projectRequests');
      const docData = {
        title: `Opt-in: Request Verve GST Compliance Portal for ${projectData.projectName || 'My Business'}`,
        category: 'GST Integration',
        priority: 'High',
        description: `Client requested activation of Verve GST Platform (gst.verveai.co) for ${projectData.projectName || 'company'}. Contact: ${projectData.primaryContactPhone || currentUser.email}. Please provision the GST workspace and provide access credentials.`,
        status: 'Submitted',
        createdAt: new Date().toISOString(),
        clientEmail: currentUser.email,
      };
      const docRef = await addDoc(reqsColRef, docData);
      setRequests(prev => [{ id: docRef.id, ...docData }, ...prev]);
      setGstRequested(true);

      // Log into client_activity_logs if clientId available
      if (clientData?.id) {
        try {
          await addDoc(collection(db, 'client_activity_logs'), {
            clientId: clientData.id,
            action: 'GST_INTEGRATION_REQUESTED',
            details: `Client opted in for Verve GST Platform integration for '${projectData.projectName || 'business'}'`,
            userEmail: 'client-portal',
            timestamp: serverTimestamp(),
          });
        } catch (e) {
          // ignore
        }
      }

      setSnackbar({
        open: true,
        message: '🎉 GST Compliance Portal integration requested! Our compliance engineers have been notified and will provision your portal.',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error requesting GST integration:', err);
      setSnackbar({
        open: true,
        message: 'Failed to submit GST request: ' + err.message,
        severity: 'error'
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#06b6d4' }} />
      </Box>
    );
  }

  // Calculate live Client Portal URLs and Domains
  const shareToken = clientData?.shareSettings?.token;
  const isShareActive = clientData?.shareSettings?.enabled !== false;
  const clientPortalUrl = shareToken
    ? `https://admin.verveailabs.com/share/${shareToken}`
    : clientData?.id
      ? `https://admin.verveailabs.com/clients/${clientData.id}`
      : null;

  const primaryDomainRaw = clientData?.domainInfo?.primaryDomain || clientData?.website || (projectData.projectName ? `${projectData.projectName.toLowerCase().replace(/[^a-z0-9]/g, '')}.com` : '');
  const primaryDomainUrl = primaryDomainRaw ? (primaryDomainRaw.startsWith('http') ? primaryDomainRaw : `https://${primaryDomainRaw}`) : null;

  const stagingDomainRaw = clientData?.domainInfo?.stagingDomain || (primaryDomainRaw ? `stage.${primaryDomainRaw.replace(/^https?:\/\//, '')}` : '');
  const stagingDomainUrl = stagingDomainRaw ? (stagingDomainRaw.startsWith('http') ? stagingDomainRaw : `https://${stagingDomainRaw}`) : null;

  const completedChecklistCount = checklists.filter(c => c.completed).length;
  const checklistPercent = checklists.length > 0 ? Math.round((completedChecklistCount / checklists.length) * 100) : 0;

  const fallbackMilestones = [
    { title: '1. Architecture & Spec Alignment', status: 'Completed', date: 'Phase 1' },
    { title: '2. UI/UX System & Interactive Prototyping', status: 'Completed', date: 'Phase 2' },
    { title: '3. Full-Stack Development & AI Orchestration', status: 'In Progress', date: 'Phase 3' },
    { title: '4. Security Rules, QA Preflight & Testing', status: 'Upcoming', date: 'Phase 4' },
    { title: '5. Production Handover & Custom Domain Launch', status: 'Upcoming', date: 'Phase 5' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {/* 1. Top Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)',
          border: '1px solid rgba(6, 182, 212, 0.25)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label="CLIENT PROJECT WORKSPACE"
                size="small"
                sx={{
                  bgcolor: 'rgba(6, 182, 212, 0.2)',
                  color: '#22d3ee',
                  fontWeight: 800,
                  fontSize: '11px',
                  border: '1px solid rgba(6, 182, 212, 0.4)',
                }}
              />
              <Chip
                label={projectData.stage}
                size="small"
                sx={{
                  bgcolor: 'rgba(16, 185, 129, 0.15)',
                  color: '#34d399',
                  fontWeight: 700,
                  fontSize: '11px',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                }}
              />
              {clientData?.domainInfo?.sslStatus === 'active' && (
                <Chip
                  icon={<ShieldIcon sx={{ fontSize: '13px !important', color: '#10b981 !important' }} />}
                  label="SSL SECURED"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.12)',
                    color: '#34d399',
                    fontWeight: 700,
                    fontSize: '10px',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                />
              )}
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: textPrimary, letterSpacing: '-0.5px', mb: 1 }}>
              {projectData.projectName || 'Startup Launchpad Project'}
            </Typography>
            <Typography variant="body1" sx={{ color: textSecondary }}>
              Welcome to your dedicated startup launchpad hub on <strong style={{ color: '#06b6d4' }}>VerveAI Launchpad</strong>. Update your project specifications, track milestones, and submit requests directly to our team.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveProject}
              disabled={saving}
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                color: '#FFFFFF',
                fontWeight: 700,
                px: 3,
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 14px rgba(6, 182, 212, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)' }
              }}
            >
              {saving ? 'Saving Changes...' : 'Save Project Updates'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 2. Active Client Portal & Provisioned URLs Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 3.5,
          borderRadius: 3,
          bgcolor: isLight ? '#f0fdf4' : 'rgba(16, 185, 129, 0.04)',
          border: isLight ? '1px solid #bbf7d0' : '1px solid rgba(16, 185, 129, 0.3)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <GlobeIcon sx={{ color: '#10b981', fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textPrimary, fontSize: '1rem' }}>
                  Active Client Delivery Portal & Live Infrastructure
                </Typography>
                <Chip
                  label="ACTIVE & PROVISIONED"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.2)',
                    color: '#10b981',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    fontWeight: 800,
                    fontSize: '9px',
                    height: '20px',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: textSecondary, fontSize: '0.82rem' }}>
                Your project URLs and real-time delivery link are provisioned by our engineering team, active even before completing all custom specifications.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          {/* Tile 1: Client Delivery Portal Link */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: bgSurface,
                border: `1px solid ${borderCard}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#06b6d4', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Client Portal Access Link
                  </Typography>
                  <Chip
                    label={isShareActive ? 'Active ✓' : 'Secured'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(6, 182, 212, 0.15)',
                      color: '#22d3ee',
                      fontSize: '9px',
                      height: '18px',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, wordBreak: 'break-all', fontSize: '0.86rem' }}>
                  {clientPortalUrl || 'https://admin.verveailabs.com/share/...'}
                </Typography>
                <Typography variant="caption" sx={{ color: textMuted, display: 'block', mt: 0.5, fontSize: '11px' }}>
                  Interactive portal to view assets, checklist tasks, and DNS status.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  component="a"
                  href={clientPortalUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                  disabled={!clientPortalUrl}
                  sx={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                    color: '#FFFFFF',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 0.6,
                    flexGrow: 1,
                  }}
                >
                  Open Portal ↗
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={() => handleCopyLink(clientPortalUrl)}
                  disabled={!clientPortalUrl}
                  sx={{
                    borderColor: '#334155',
                    color: textSecondary,
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    py: 0.6,
                    '&:hover': { borderColor: '#06b6d4', color: '#06b6d4' }
                  }}
                >
                  {copiedLink ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Tile 2: Live Production Domain */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: bgSurface,
                border: `1px solid ${borderCard}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Production Web Storefront
                  </Typography>
                  <Chip
                    label={clientData?.domainInfo?.dnsStatus === 'propagated' ? 'DNS Propagated' : 'Live'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(16, 185, 129, 0.15)',
                      color: '#34d399',
                      fontSize: '9px',
                      height: '18px',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                  {primaryDomainRaw || 'Custom Domain Pending'}
                </Typography>
                <Typography variant="caption" sx={{ color: textMuted, display: 'block', mt: 0.5, fontSize: '11px' }}>
                  Cloudflare SSL encrypted & sub-second loading.
                </Typography>
              </Box>

              <Button
                component="a"
                href={primaryDomainUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                disabled={!primaryDomainUrl}
                sx={{
                  borderColor: 'rgba(16, 185, 129, 0.4)',
                  color: '#10b981',
                  bgcolor: 'rgba(16, 185, 129, 0.05)',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 0.6,
                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)', borderColor: '#10b981' }
                }}
              >
                Visit Live Site ↗
              </Button>
            </Box>
          </Grid>

          {/* Tile 3: Staging / Prototype Environment */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: bgSurface,
                border: `1px solid ${borderCard}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#a855f7', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Staging / Cloud Run URL
                  </Typography>
                  <Chip
                    label="Cloud Run Active"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(168, 85, 247, 0.15)',
                      color: '#c084fc',
                      fontSize: '9px',
                      height: '18px',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                  {stagingDomainRaw || 'stage.gaonpure.com'}
                </Typography>
                <Typography variant="caption" sx={{ color: textMuted, display: 'block', mt: 0.5, fontSize: '11px' }}>
                  Isolated testing environment for rapid staging iterations.
                </Typography>
              </Box>

              <Button
                component="a"
                href={stagingDomainUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                disabled={!stagingDomainUrl}
                sx={{
                  borderColor: 'rgba(168, 85, 247, 0.4)',
                  color: '#c084fc',
                  bgcolor: 'rgba(168, 85, 247, 0.05)',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 0.6,
                  '&:hover': { bgcolor: 'rgba(168, 85, 247, 0.15)', borderColor: '#a855f7' }
                }}
              >
                Preview Staging ↗
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 3. Verve GST Compliance Platform Highlight & Opt-In Banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 3.5 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.09) 0%, rgba(59, 130, 246, 0.05) 50%, rgba(16, 185, 129, 0.08) 100%)',
          border: '1px solid rgba(6, 182, 212, 0.35)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(6, 182, 212, 0.15)', px: 1.5, py: 0.4, borderRadius: '999px', mb: 1.5, border: '1px solid rgba(6, 182, 212, 0.3)' }}>
              <ReceiptIcon sx={{ fontSize: 14, color: '#22d3ee' }} />
              <Typography variant="caption" sx={{ color: '#22d3ee', fontWeight: 800, fontSize: '10px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                RECOMMENDED OPERATIONAL ADD-ON • B2B COMPLIANCE
              </Typography>
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, color: textPrimary, fontSize: { xs: '1.25rem', md: '1.45rem' }, mb: 1, letterSpacing: '-0.02em' }}>
              Automate Your B2B Invoicing & Tax Filing with Verve GST Portal
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary, lineHeight: 1.6, mb: 2.5, maxWidth: '720px' }}>
              Setting up your business entity? Eliminate compliance friction from Day 1. Verve GST Portal offers automated E-Invoicing, instant GSTR-1 and GSTR-2B reconciliation, ITC leak defense, and zero-effort CA collaboration—seamlessly integrated with your new brand.
            </Typography>

            <Grid container spacing={1.5}>
              {[
                '1-Click Automated E-Invoicing & E-Way Bills',
                'GSTR-1 & GSTR-2B AI Auto-Reconciliation',
                'ITC Mismatch & Late Fee Protection',
                `Auto-Synced with ${projectData.projectName || 'Your Business'}`,
              ].map((perk) => (
                <Grid item xs={12} sm={6} key={perk}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                    <Typography variant="caption" sx={{ color: textPrimary, fontWeight: 600, fontSize: '0.82rem' }}>
                      {perk}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} lg={4} sx={{ textAlign: { xs: 'left', lg: 'right' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: { xs: 'flex-start', lg: 'flex-end' } }}>
              <Button
                component="a"
                href="https://gst.verveai.co"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                endIcon={<OpenInNewIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(6, 182, 212, 0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #22d3ee 0%, #0369a1 100%)' }
                }}
              >
                Launch GST Platform ↗
              </Button>

              <Button
                onClick={handleRequestGstIntegration}
                variant="outlined"
                size="medium"
                disabled={gstRequested}
                startIcon={gstRequested ? <CheckIcon /> : <SparklesIcon />}
                sx={{
                  borderColor: gstRequested ? '#10b981' : 'rgba(6, 182, 212, 0.4)',
                  color: gstRequested ? '#34d399' : '#22d3ee',
                  bgcolor: gstRequested ? 'rgba(16, 185, 129, 0.1)' : 'rgba(6, 182, 212, 0.06)',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  textTransform: 'none',
                  px: 2.2,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'rgba(6, 182, 212, 0.15)',
                    borderColor: '#06b6d4',
                  }
                }}
              >
                {gstRequested ? '✓ GST Integration Requested' : 'Opt-In: Request GST Setup for My Business'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 4. Main Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: borderCard, mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            '& .MuiTab-root': {
              color: textSecondary,
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'none',
              '&.Mui-selected': { color: '#06b6d4' }
            },
            '& .MuiTabs-indicator': { backgroundColor: '#06b6d4' }
          }}
        >
          <Tab label="Project Details & Specs" icon={<ProjectIcon />} iconPosition="start" />
          <Tab
            label={`Project Updates & Activity (${activityLogs.length})`}
            icon={<HistoryIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Project Requests (${requests.length})`}
            icon={<AddIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Milestones & Roadmap (${checklists.length > 0 ? `${completedChecklistCount}/${checklists.length}` : '5 Phases'})`}
            icon={<CheckCircleIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Invoices & Billing (${invoices.length})`}
            icon={<ReceiptIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* TAB 0: Project Details & Specs */}
      {activeTab === 0 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: bgSurface,
                border: `1px solid ${borderCard}`,
                borderRadius: 3,
              }}
            >
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}>
                Startup & Project Information
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary, mb: 3 }}>
                Provide or update specifications for your engineering team. These details guide our technical architects during sprints.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    label="Project / Startup Name"
                    name="projectName"
                    value={projectData.projectName}
                    onChange={handleProjectFieldChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    label="Current Stage"
                    name="stage"
                    value={projectData.stage}
                    onChange={handleProjectFieldChange}
                    helperText="e.g. Discovery, In Development, Testing, Live"
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="One-line Pitch / Vision"
                    name="tagline"
                    value={projectData.tagline}
                    onChange={handleProjectFieldChange}
                    multiline
                    rows={2}
                    helperText="Brief summary of what your application or AI tool does"
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Target Market / Ideal Customer Profile"
                    name="targetAudience"
                    value={projectData.targetAudience}
                    onChange={handleProjectFieldChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Core Technical Scope & Key Features"
                    name="techScope"
                    value={projectData.techScope}
                    onChange={handleProjectFieldChange}
                    multiline
                    rows={4}
                    helperText="Specific features, third-party APIs, or integrations you require"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    label="Staging / Prototype URL"
                    name="stagingUrl"
                    value={projectData.stagingUrl}
                    onChange={handleProjectFieldChange}
                    placeholder="https://preview.verveai.co/your-app"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    label="Repository / Asset Link"
                    name="repoUrl"
                    value={projectData.repoUrl}
                    onChange={handleProjectFieldChange}
                    placeholder="https://github.com/..."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    label="Primary Contact Person"
                    name="primaryContactName"
                    value={projectData.primaryContactName}
                    onChange={handleProjectFieldChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    label="Primary Contact Phone / WhatsApp"
                    name="primaryContactPhone"
                    value={projectData.primaryContactPhone}
                    onChange={handleProjectFieldChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Additional Notes / Architecture Remarks"
                    name="notes"
                    value={projectData.notes}
                    onChange={handleProjectFieldChange}
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProject}
                  disabled={saving}
                  sx={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    px: 3,
                    py: 1.2
                  }}
                >
                  {saving ? 'Saving...' : 'Save Project Details'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar Info Card & Recent Updates Mini-Feed */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Dedicated Engineering Team */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: bgSurface,
                  border: `1px solid ${borderCard}`,
                  borderRadius: 3,
                }}
              >
                <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}>
                  Dedicated Engineering Team
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
                  Your startup is supported by the technical architects and engineers at <strong style={{ color: '#06b6d4' }}>Verve AI Labs</strong>.
                </Typography>
                <Divider sx={{ my: 2, borderColor: borderSubtle }} />
                <Typography variant="caption" sx={{ color: textMuted, display: 'block', mb: 0.5, fontWeight: 700 }}>
                  OFFICIAL COMMUNICATION CHANNEL
                </Typography>
                <Typography variant="body2" sx={{ color: textPrimary, mb: 2 }}>
                  admin@verveai.co
                </Typography>
                <Typography variant="caption" sx={{ color: textMuted, display: 'block', mb: 0.5, fontWeight: 700 }}>
                  SUPPORT PHONE / WHATSAPP
                </Typography>
                <Typography variant="body2" sx={{ color: '#10b981', mb: 2, fontWeight: 600 }}>
                  +91 9415967302
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  component="a"
                  href="mailto:admin@verveai.co"
                  startIcon={<EmailIcon />}
                  sx={{
                    borderColor: borderCard,
                    color: '#06b6d4',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: '#06b6d4' }
                  }}
                >
                  Email Lead Architect
                </Button>
              </Paper>

              {/* Recent Activity Mini-Feed */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: bgSurface,
                  border: `1px solid ${borderCard}`,
                  borderRadius: 3,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 700, fontSize: '0.95rem' }}>
                    Latest Project Updates
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setActiveTab(1)}
                    sx={{ color: '#06b6d4', fontSize: '0.78rem', textTransform: 'none', fontWeight: 600 }}
                  >
                    View All →
                  </Button>
                </Box>

                {activityLogs.length === 0 ? (
                  <Typography variant="body2" sx={{ color: textMuted, fontSize: '0.82rem' }}>
                    No recent activity logged yet. Updates appear here as our engineers advance your project.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {activityLogs.slice(0, 3).map((log) => {
                      const dateStr = log.timestamp?.seconds
                        ? new Date(log.timestamp.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : 'Recent';
                      return (
                        <Box
                          key={log.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: bgCardSubtle,
                            border: `1px solid ${borderSubtle}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Chip
                              label={log.action?.replace(/_/g, ' ') || 'Update'}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(6, 182, 212, 0.12)',
                                color: '#22d3ee',
                                fontSize: '9px',
                                height: '18px',
                                fontWeight: 700
                              }}
                            />
                            <Typography variant="caption" sx={{ color: textMuted, fontSize: '10px' }}>
                              {dateStr}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: textPrimary, fontSize: '0.82rem', lineHeight: 1.4 }}>
                            {log.details}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}

      {/* TAB 1: Live Project Updates & Activity */}
      {activeTab === 1 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: bgSurface,
            border: `1px solid ${borderCard}`,
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
                Real-Time Project Updates & Engineering Log
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                All technical deployments, domain configurations, milestone completions, and team deliverables recorded in real-time.
              </Typography>
            </Box>
            <Chip
              icon={<HistoryIcon sx={{ fontSize: '14px !important', color: '#06b6d4 !important' }} />}
              label={`${activityLogs.length} Events Logged`}
              sx={{ bgcolor: 'rgba(6, 182, 212, 0.12)', color: '#22d3ee', fontWeight: 700 }}
            />
          </Box>

          {activityLogs.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center', border: `1px dashed ${borderCard}`, borderRadius: 2 }}>
              <HistoryIcon sx={{ fontSize: 44, color: textMuted, mb: 1.5 }} />
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}>
                Engineering Setup in Progress
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary, maxWidth: 500, mx: 'auto' }}>
                Your project has been received. As our team provisions cloud infrastructure, DNS records, and AI models, live logs will stream into this feed.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {activityLogs.map((log) => {
                const dateObj = log.timestamp?.seconds
                  ? new Date(log.timestamp.seconds * 1000)
                  : (log.timestamp ? new Date(log.timestamp) : null);
                const fullDateStr = dateObj
                  ? dateObj.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : 'Recent';

                return (
                  <Box
                    key={log.id}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: bgCardSubtle,
                      border: `1px solid ${borderCard}`,
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: '#06b6d4' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={log.action?.replace(/_/g, ' ') || 'UPDATE'}
                          size="small"
                          sx={{
                            bgcolor: log.action?.includes('DOMAIN')
                              ? 'rgba(16, 185, 129, 0.15)'
                              : log.action?.includes('SHARE')
                                ? 'rgba(6, 182, 212, 0.15)'
                                : 'rgba(168, 85, 247, 0.15)',
                            color: log.action?.includes('DOMAIN')
                              ? '#34d399'
                              : log.action?.includes('SHARE')
                                ? '#22d3ee'
                                : '#c084fc',
                            fontWeight: 800,
                            fontSize: '10px',
                          }}
                        />
                        <Typography variant="caption" sx={{ color: textMuted, fontSize: '11px' }}>
                          Updated by {log.adminEmail || 'Verve AI Engineering Team'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 600 }}>
                        {fullDateStr}
                      </Typography>
                    </Box>

                    <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 500, fontSize: '0.92rem' }}>
                      {log.details}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* TAB 2: Project Requests */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
                Project Requests & Change Orders
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                Submit technical questions, custom API integrations, or copy changes directly to your assigned architect.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddRequest(!showAddRequest)}
              sx={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                color: '#FFFFFF',
                fontWeight: 700
              }}
            >
              {showAddRequest ? 'Cancel' : 'Submit New Request'}
            </Button>
          </Box>

          {showAddRequest && (
            <Paper
              component="form"
              onSubmit={handleCreateRequest}
              elevation={0}
              sx={{
                p: 4,
                mb: 4,
                bgcolor: bgSurface,
                border: '1px solid #06b6d4',
                borderRadius: 3,
              }}
            >
              <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 700, mb: 2 }}>
                New Request or Feature Specification
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    required
                    label="Request Title"
                    value={newRequest.title}
                    onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                    placeholder="e.g. Integrate custom webhook listener or payment gateway"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    label="Category"
                    value={newRequest.category}
                    onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                    helperText="Feature / Bug / UI / Integration / GST"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CustomTextField
                    label="Priority"
                    value={newRequest.priority}
                    onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value })}
                    helperText="Standard / High / Urgent"
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    required
                    label="Detailed Description & Acceptance Criteria"
                    value={newRequest.description}
                    onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                    multiline
                    rows={4}
                    placeholder="Describe exactly what needs to be changed, added, or tested..."
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={() => setShowAddRequest(false)} sx={{ borderColor: borderCard, color: textSecondary }}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={submittingRequest} sx={{ bgcolor: '#06b6d4', color: '#07090E', fontWeight: 700 }}>
                  {submittingRequest ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Paper>
          )}

          {requests.length === 0 ? (
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', bgcolor: bgSurface, border: `1px dashed ${borderCard}`, borderRadius: 3 }}>
              <ScheduleIcon sx={{ fontSize: 48, color: textMuted, mb: 2 }} />
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}>
                No Requests Submitted Yet
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary, mb: 3 }}>
                Need a feature adjusted, a new API integrated, or design fine-tuned? Submit your request above.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {requests.map((req) => (
                <Paper
                  key={req.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: bgSurface,
                    border: `1px solid ${borderCard}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
                        {req.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={req.category} size="small" sx={{ bgcolor: bgCardSubtle, color: textPrimary, fontSize: '11px' }} />
                        <Chip
                          label={req.priority}
                          size="small"
                          sx={{
                            bgcolor: req.priority === 'Urgent' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(6, 182, 212, 0.15)',
                            color: req.priority === 'Urgent' ? '#f87171' : '#22d3ee',
                            fontSize: '11px'
                          }}
                        />
                      </Box>
                    </Box>
                    <Chip
                      label={req.status || 'Submitted'}
                      sx={{
                        bgcolor: req.status === 'Completed' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                        color: req.status === 'Completed' ? '#34d399' : '#fbbf24',
                        fontWeight: 700
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: textSecondary, whiteSpace: 'pre-wrap' }}>
                    {req.description}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* TAB 3: Milestones & Roadmap (With Real Checklist Integration) */}
      {activeTab === 3 && (
        <Paper elevation={0} sx={{ p: 4, bgcolor: bgSurface, border: `1px solid ${borderCard}`, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
                {checklists.length > 0 ? 'Verified Project Deliverables & Checklists' : 'Startup Launchpad Roadmap'}
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                {checklists.length > 0
                  ? 'Real-time verification of deliverables provisioned and signed off by the engineering team.'
                  : 'Standard 5-phase delivery trajectory for turnkey launchpad onboarding.'}
              </Typography>
            </Box>

            {checklists.length > 0 && (
              <Box sx={{ minWidth: 200 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700 }}>
                    Completion Progress
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 800 }}>
                    {completedChecklistCount}/{checklists.length} ({checklistPercent}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={checklistPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #06b6d4 0%, #10b981 100%)',
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          {checklists.length > 0 ? (
            <Stack spacing={2}>
              {checklists.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    p: 2.2,
                    borderRadius: 2,
                    bgcolor: item.completed ? 'rgba(16, 185, 129, 0.06)' : bgCardSubtle,
                    border: '1px solid',
                    borderColor: item.completed ? 'rgba(16, 185, 129, 0.3)' : borderCard,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {item.completed ? (
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 26 }} />
                    ) : (
                      <ScheduleIcon sx={{ color: '#64748B', fontSize: 26 }} />
                    )}
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 600, fontSize: '0.95rem' }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                        {item.category && (
                          <Chip
                            label={item.category.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(6, 182, 212, 0.1)',
                              color: '#22d3ee',
                              fontSize: '9px',
                              height: '18px',
                              fontWeight: 700,
                            }}
                          />
                        )}
                        {item.serviceType && (
                          <Typography variant="caption" sx={{ color: textMuted, fontSize: '11px' }}>
                            • {item.serviceType}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Chip
                    label={item.completed ? 'Verified ✓' : 'In Progress'}
                    size="small"
                    sx={{
                      bgcolor: item.completed ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      color: item.completed ? '#34d399' : textMuted,
                      fontWeight: 700,
                    }}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              {fallbackMilestones.map((ms) => (
                <Box
                  key={ms.title}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: ms.status === 'Completed' ? 'rgba(16, 185, 129, 0.06)' : ms.status === 'In Progress' ? 'rgba(6, 182, 212, 0.08)' : bgCardSubtle,
                    border: '1px solid',
                    borderColor: ms.status === 'Completed' ? 'rgba(16, 185, 129, 0.3)' : ms.status === 'In Progress' ? 'rgba(6, 182, 212, 0.35)' : borderCard,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {ms.status === 'Completed' ? (
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 28 }} />
                    ) : ms.status === 'In Progress' ? (
                      <ScheduleIcon sx={{ color: '#06b6d4', fontSize: 28 }} />
                    ) : (
                      <ScheduleIcon sx={{ color: textMuted, fontSize: 28 }} />
                    )}
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 600 }}>
                        {ms.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textMuted }}>
                        {ms.date}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={ms.status}
                    size="small"
                    sx={{
                      bgcolor: ms.status === 'Completed' ? 'rgba(16, 185, 129, 0.2)' : ms.status === 'In Progress' ? 'rgba(6, 182, 212, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      color: ms.status === 'Completed' ? '#34d399' : ms.status === 'In Progress' ? '#22d3ee' : textMuted,
                      fontWeight: 700
                    }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {/* TAB 4: Invoices & Payment Settlements */}
      {activeTab === 4 && (
        <ClientInvoicesTab
          invoices={invoices}
          loading={loadingInvoices}
          clientId={clientData?.id}
          currentUser={currentUser}
          onInvoiceUpdated={(updatedInv) => {
            setInvoices(prev => prev.map(inv => inv.id === updatedInv.id ? updatedInv : inv));
            setSnackbar({
              open: true,
              message: `✓ Remittance UTR #${updatedInv.clientUtrSubmission?.utrNumber} submitted! Verve AI accounts desk will reconcile and verify with IndusInd Bank.`,
              severity: 'success'
            });
          }}
        />
      )}

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
